import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { t } from '../translations/translations';
import { Badge } from './ui';
import { MenuIcon, ShoppingCartIcon, XIcon } from './ui/icons';

const Header = () => {
  const { language, toggleLanguage, isCroatian } = useLanguage();
  const { setIsCartOpen, getCartCount } = useCart();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const cartCount = getCartCount();
  const navItems = useMemo(
    () => [
      { to: '/', label: t('home', language), end: true },
      { to: '/shop', label: t('shop', language) },
      { to: '/blog', label: t('blog', language) },
      { to: '/contact', label: t('contact', language) },
    ],
    [language]
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const desktopNavClasses = ({ isActive }) =>
    `px-4 py-2 rounded-lg font-medium transition-all no-underline ${
      isActive
        ? 'text-gold bg-bronze/20 border border-bronze/40'
        : 'text-light-secondary border border-transparent hover:text-light hover:bg-dark-lighter/80 hover:border-dark-border'
    }`;

  const mobileNavClasses = ({ isActive }) =>
    `block w-full px-4 py-3.5 rounded-lg text-left text-base font-semibold transition-all no-underline ${
      isActive
        ? 'bg-bronze/20 text-gold border border-bronze/40'
        : 'text-light-secondary border border-dark-border hover:border-bronze/40 hover:bg-dark-lighter'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-30 border-b border-dark-border bg-dark transition-all duration-300 ${
        isMobileMenuOpen || isScrolled ? 'shadow-lg lg:bg-dark/95 lg:backdrop-blur-md' : 'lg:bg-dark/60 lg:backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="/logohistorio.png"
              alt="Historio Logo"
              className="h-7 w-7 sm:h-10 sm:w-10 object-contain"
            />
            <span className="text-lg sm:text-2xl font-serif text-gradient-bronze font-bold">
              Historio
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-2 rounded-xl border border-dark-border bg-dark/70 px-2 py-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={Boolean(item.end)}
                className={desktopNavClasses}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative inline-flex h-10 w-10 items-center justify-center text-light-secondary transition-colors border border-dark-border rounded-lg hover:text-bronze hover:border-bronze/50 hover:bg-dark-lighter/80"
              aria-label={t('shoppingCart', language)}
            >
              <ShoppingCartIcon className="w-5 h-5" />
              {cartCount > 0 && (
                <Badge
                  variant="default"
                  size="sm"
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center"
                >
                  {cartCount}
                </Badge>
              )}
            </button>

            <button
              onClick={toggleLanguage}
              className="px-3 py-2 text-bronze hover:text-gold transition-colors font-medium border border-bronze rounded-lg hover:bg-bronze/10 text-sm"
              title={t('language', language)}
            >
              {isCroatian ? 'HR' : 'EN'}
            </button>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-dark-border rounded-lg text-light-secondary hover:text-bronze hover:border-bronze/50 hover:bg-dark-lighter/80 transition-colors"
              aria-label={isMobileMenuOpen ? t('closeMenu', language) : t('openMenu', language)}
            >
              {isMobileMenuOpen ? <XIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`lg:hidden fixed inset-0 top-16 sm:top-20 z-40 bg-black/70 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileMenu}
      >
        <div
          className="absolute inset-x-0 top-0 h-full bg-dark px-4 py-5 shadow-2xl sm:px-6 md:left-auto md:w-[430px] md:border-l md:border-dark-border"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="mb-4 text-xs uppercase tracking-[0.18em] text-light-muted">
            {t('menu', language)}
          </p>

          <div className="space-y-2.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={Boolean(item.end)}
                onClick={closeMobileMenu}
                className={mobileNavClasses}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
